import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Icon, IconButton, Input, Modal, Select, Textarea, useToast } from "@astrum/ui";
import { bytesToMb, DURATION_HINT, parseDuration, uploadLimitFor } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import type { Milestone, ProjectSection, Tag, Task } from "../lib/api.js";
import { TagPicker } from "./TagPicker.js";
import { TaskTemplatePicker, type TaskTemplateCard } from "./Lifecycle.js";
import { uploadFile, readUploadResponse } from "../lib/transport.js";
import { uploadFailure, uploadThrownMessage } from "../lib/upload-error.js";
import { downscaleImage } from "../lib/downscale-image.js";
import { useFileDrop } from "../lib/useFileDrop.js";
import "./new-task-dialog.css";

/**
 * Creating a task properly — brief, owner, dates, and attachments.
 *
 * The inline "+ Add task" rows stay for the case they are good at: capturing a
 * title quickly while thinking about something else. This is for the other
 * case, where a task is being handed to someone and needs to carry enough
 * context that they do not have to come and ask.
 *
 * Shared rather than per-surface: Work, project detail, and chat all create
 * tasks, and three dialogs that drift apart is how "assignee" ends up meaning
 * something different in each.
 */

export interface NewTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (task: Task) => void;
  /** Pre-selected project. When absent the dialog asks for one. */
  projectId?: string | null;
  /** Column the task lands in when raised from a board. */
  status?: string;
  /** Pre-filled title, e.g. from a chat message. */
  initialTitle?: string;
  /** Links the task back to the message it came from. */
  sourceMessageId?: string;
}

type Person = { id: string; displayName: string; avatarUrl: string | null; jobTitle: string | null };
type ProjectOption = { id: string; code: string; name: string };

/**
 * The rhythms worth offering, as the RRULEs they are stored as.
 *
 * Matches TaskDetail.tsx's own list exactly — a task created here and one
 * edited there should offer the same choices, not two dropdowns that happen
 * to look alike.
 */
const RECURRENCE_PRESETS: { label: string; rule: string | null }[] = [
  { label: "Does not repeat", rule: null },
  { label: "Every day", rule: "FREQ=DAILY" },
  { label: "Every week", rule: "FREQ=WEEKLY" },
  { label: "Every two weeks", rule: "FREQ=WEEKLY;INTERVAL=2" },
  { label: "Every month", rule: "FREQ=MONTHLY" },
  { label: "Every three months", rule: "FREQ=MONTHLY;INTERVAL=3" },
  { label: "Every year", rule: "FREQ=YEARLY" },
];

export function NewTaskDialog({
  open,
  onClose,
  onCreated,
  projectId,
  status = "todo",
  initialTitle = "",
  sourceMessageId,
}: NewTaskDialogProps) {
  const { toast } = useToast();

  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState("");
  /** Every owner, not just one — the same `task_assignees` model TaskDetail.tsx
   *  edits after creation, offered up front so a task headed to several
   *  people does not need an immediate follow-up edit. */
  const [ownerIds, setOwnerIds] = useState<string[]>([]);
  const [priority, setPriority] = useState("normal");
  const [dueAt, setDueAt] = useState("");
  const [estimate, setEstimate] = useState("");
  const [recurrenceRule, setRecurrenceRule] = useState<string | null>(null);
  const [chosenProject, setChosenProject] = useState(projectId ?? "");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [sectionId, setSectionId] = useState("");
  const [milestoneId, setMilestoneId] = useState("");
  const [sections, setSections] = useState<ProjectSection[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [saving, setSaving] = useState(false);
  /** Template the form was filled from; the server adds its checklist and tags. */
  const [templateId, setTemplateId] = useState<string | null>(null);

  const { isOver: isDraggingFilesOver, dropZoneProps: fileDropZoneProps } = useFileDrop(
    useCallback((dropped) => setPendingFiles((prev) => [...prev, ...dropped]), []),
  );

  const [people, setPeople] = useState<Person[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);

  /* Reset when reopened: a dialog that remembers the last task's assignee
   * silently attributes work to the wrong person. */
  useEffect(() => {
    if (!open) return;
    setTitle(initialTitle);
    setDescription("");
    setOwnerIds([]);
    setPriority("normal");
    setDueAt("");
    setEstimate("");
    setRecurrenceRule(null);
    setChosenProject(projectId ?? "");
    setPendingFiles([]);
    setTags([]);
    setSectionId("");
    setMilestoneId("");
    setTemplateId(null);
  }, [open, initialTitle, projectId]);

  /* Sections and milestones belong to a project, so they are offered once one
   * is chosen and cleared when it changes. */
  useEffect(() => {
    setSectionId("");
    setMilestoneId("");
    setSections([]);
    setMilestones([]);
    if (!open || !chosenProject) return;
    let cancelled = false;
    api
      .get<{ sections: ProjectSection[] }>(`/projects/${chosenProject}/sections`)
      .then((res) => !cancelled && setSections(res.sections))
      .catch(() => undefined);
    api
      .get<{ milestones: Milestone[] }>(`/projects/${chosenProject}/milestones`)
      .then((res) => !cancelled && setMilestones(res.milestones.filter((m) => m.status !== "done")))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, chosenProject]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    api
      .get<{ people: Person[] }>("/people")
      .then((res) => !cancelled && setPeople(res.people))
      .catch(() => undefined);

    // Only needed when the caller has not fixed the project.
    if (!projectId) {
      api
        .get<{ projects: ProjectOption[] }>("/projects")
        .then((res) => !cancelled && setProjects(res.projects))
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const canSubmit = useMemo(() => title.trim().length > 0 && !saving, [title, saving]);

  const submit = useCallback(() => {
    const trimmed = title.trim();
    if (!trimmed) return;

    // Refuse rather than silently drop: somebody who typed "2 weeks" should be
    // told the units, not have their estimate quietly discarded.
    const estimateParsed = parseDuration(estimate);
    if (!estimateParsed.ok) {
      toast(estimateParsed.error, { tone: "danger" });
      return;
    }

    // Client-side mirror of the server's rule: a repeat is measured from the
    // due date, so one without a due date has nothing to count from.
    if (recurrenceRule && !dueAt) {
      toast("A repeating task needs a due date.", { tone: "danger" });
      return;
    }

    setSaving(true);

    api
      .post<{ task: Task }>("/tasks", {
        title: trimmed,
        description: description.trim() || undefined,
        projectId: chosenProject || null,
        status,
        priority,
        assigneeIds: ownerIds.length ? ownerIds : undefined,
        // Midnight UTC — a calendar date carries no timezone and must not
        // acquire one on the way to the server.
        dueAt: dueAt ? `${dueAt}T00:00:00.000Z` : undefined,
        // Parsed rather than Number()'d: the field takes "3d" now, and the
        // old cast turned anything with a unit into NaN, which serialises to
        // null and trips the schema. Unparseable input is left out entirely
        // rather than sent as zero — an estimate nobody could read is not an
        // estimate of nothing.
        estimateMinutes: estimateParsed.ok && estimateParsed.minutes > 0 ? estimateParsed.minutes : undefined,
        recurrenceRule,
        sourceMessageId,
        tagIds: tags.length ? tags.map((t) => t.id) : undefined,
        templateId: templateId ?? undefined,
        sectionId: sectionId || undefined,
        milestoneId: milestoneId || undefined,
      })
      .then(async (res) => {
        /*
         * Attachments upload after the task exists, because they need its id.
         * Uploaded in sequence rather than in parallel: each one is scanned,
         * and a burst of concurrent scans is the one thing that reliably
         * exhausts clamd on a small server.
         */
        for (const file of pendingFiles) {
          // Skip an over-limit file with a clear reason rather than sending it
          // to be refused.
          const limit = uploadLimitFor(file.type);
          if (file.size > limit) {
            toast(`${file.name}: files must be under ${bytesToMb(limit)}MB.`, { tone: "danger" });
            continue;
          }
          // Same preparation as every other uploader: compress photos and strip
          // the metadata (e.g. an AI image's embedded C2PA/SVG) that the firewall
          // would otherwise block with a 403.
          const prepared = await downscaleImage(file);
          const form = new FormData();
          form.append("taskId", res.task.id);
          form.append("file", prepared);
          try {
            const result = await readUploadResponse(await uploadFile("/files", form));
            if (!result.ok) throw uploadFailure(result);
          } catch (error) {
            // The task is already created and useful; a failed attachment is
            // reported rather than discarding everything.
            toast(`${file.name}: ${uploadThrownMessage(error)}`, { tone: "danger" });
          }
        }

        onCreated(res.task);
        onClose();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not create that task.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  }, [
    title,
    description,
    chosenProject,
    status,
    priority,
    ownerIds,
    dueAt,
    estimate,
    recurrenceRule,
    pendingFiles,
    sourceMessageId,
    tags,
    sectionId,
    milestoneId,
    onCreated,
    onClose,
    toast,
  ]);

  return (
    <Modal open={open} onClose={onClose} title="New task">
      <form
        className="new-task"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <TaskTemplatePicker
          value={templateId}
          onPick={(t: TaskTemplateCard | null) => {
            setTemplateId(t?.id ?? null);
            if (!t) return;
            setTitle(t.title);
            setDescription(t.description ?? "");
            setPriority(t.priority);
            if (t.estimateMinutes) setEstimate(`${t.estimateMinutes}m`);
          }}
        />

        <Input
          label="Task"
          placeholder="Audit telemetry coverage across depots"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          required
        />

        <Textarea
          label="Brief"
          hint="What needs doing, and what does done look like? The person picking this up reads this first."
          placeholder="Check every depot reports a heartbeat in the last 24h. Flag any that don't, with firmware versions."
          value={description}
          maxRows={6}
          onChange={(e) => setDescription(e.target.value)}
        />

        {!projectId && (
          <Select label="Project" value={chosenProject} onChange={(e) => setChosenProject(e.target.value)}>
            <option value="">No project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.code} · {project.name}
              </option>
            ))}
          </Select>
        )}

        {chosenProject && (sections.length > 0 || milestones.length > 0) && (
          <div className="new-task__row">
            {sections.length > 0 && (
              <Select label="Section" value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
                <option value="">No section</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}
                  </option>
                ))}
              </Select>
            )}
            {milestones.length > 0 && (
              <Select label="Milestone" value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)}>
                <option value="">No milestone</option>
                {milestones.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            )}
          </div>
        )}

        {/*
         * Every owner, not just one — the same task_assignees model
         * TaskDetail.tsx edits after the fact, offered here so a task headed
         * to several people does not need an immediate follow-up edit.
         */}
        <div className="new-task__field">
          <span className="new-task__label">{ownerIds.length > 1 ? "Owners" : "Assignee"}</span>
          <div className="new-task__owners">
            {ownerIds.map((id) => {
              const person = people.find((p) => p.id === id);
              return (
                <span key={id} className="new-task__owner">
                  <Avatar name={person?.displayName ?? "?"} src={person?.avatarUrl ?? undefined} size={20} />
                  <span>{person?.displayName ?? "Unknown"}</span>
                  <IconButton
                    icon={<Icon name="close" size={13} />}
                    label={`Remove ${person?.displayName ?? "this person"}`}
                    size="sm"
                    onClick={() => setOwnerIds((prev) => prev.filter((existing) => existing !== id))}
                  />
                </span>
              );
            })}
            <Select
              aria-label="Add an owner"
              value=""
              onChange={(e) => {
                const id = e.target.value;
                if (!id) return;
                setOwnerIds((prev) => [...prev, id]);
              }}
            >
              <option value="">{ownerIds.length === 0 ? "Unassigned — add someone…" : "Add someone…"}</option>
              {people
                .filter((person) => !ownerIds.includes(person.id))
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.displayName}
                    {person.jobTitle ? ` · ${person.jobTitle}` : ""}
                  </option>
                ))}
            </Select>
          </div>
        </div>

        <div className="new-task__field">
          <span className="new-task__label">Tags</span>
          <TagPicker value={tags} onChange={setTags} />
        </div>

        <div className="new-task__row">
          <Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </Select>

          <Input label="Due date" type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
        </div>

        <div className="new-task__row">
          {/* Same units as the task screen — a field that means minutes here
              and durations there is how "2" ends up meaning two of something
              nobody agreed on. */}
          <Input
            label="Estimate"
            placeholder="e.g. 3d, 4h 30m"
            value={estimate}
            onChange={(e) => setEstimate(e.target.value)}
            hint={DURATION_HINT}
          />

          {/*
           * Offered as a handful of ordinary rhythms rather than an RRULE
           * box — the string is a storage format, not something to ask
           * anyone to write. Disabled without a due date because the rule is
           * measured from it; the hint underneath says why.
           */}
          <Select
            label="Repeats"
            value={recurrenceRule ?? ""}
            disabled={!dueAt}
            onChange={(e) => setRecurrenceRule(e.target.value || null)}
          >
            {RECURRENCE_PRESETS.map((preset) => (
              <option key={preset.label} value={preset.rule ?? ""}>
                {preset.label}
              </option>
            ))}
          </Select>
        </div>
        {!dueAt && <p className="new-task__hint">Give this a due date to make it repeat.</p>}

        <div className={`new-task__files ${isDraggingFilesOver ? "is-drop-target" : ""}`} {...fileDropZoneProps}>
          <span className="new-task__label">
            Supporting files <span>{pendingFiles.length ? `· ${pendingFiles.length}` : "· optional"}</span>
          </span>

          {pendingFiles.length > 0 && (
            <ul className="new-task__file-list">
              {pendingFiles.map((file, index) => (
                <li key={`${file.name}-${index}`}>
                  <Icon name="document" size={14} />
                  <span>{file.name}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Icon name="close" size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <label className="new-task__file-add">
            <Icon name="attach" size={14} />
            Add a file
            <input
              type="file"
              multiple
              onChange={(e) => {
                setPendingFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])]);
                e.target.value = "";
              }}
            />
          </label>
          <span className="new-task__hint">
            Attached once the task is created, and scanned before anyone can open them.
          </span>
        </div>

        <div className="new-task__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} disabled={!canSubmit}>
            Create task
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Small helper so callers can show who a task went to without re-fetching. */
export function AssigneeChip({ name, avatarUrl }: { name: string | null; avatarUrl?: string | null }) {
  if (!name) return null;
  return (
    <span className="new-task__assignee">
      <Avatar name={name} src={avatarUrl ?? undefined} size={18} />
      {name}
    </span>
  );
}
